import { useMutation, useQueryClient } from '@tanstack/react-query';

import { GET_PROMPTS_QUERY_KEY } from './useGetPrompts';

import PromptsService, {
  FilterPromptsDto,
  Prompt,
  CreatePromptDto,
} from '@/services/PromptsService';

export function useUpdatePrompt(filters: FilterPromptsDto) {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Partial<CreatePromptDto>;
    }) => {
      const result = await PromptsService.updatePrompt(id, {
        ...data,
        main: false,
      });
      return result;
    },
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({
        queryKey: GET_PROMPTS_QUERY_KEY(filters),
      });

      const previousPrompts = queryClient.getQueryData(
        GET_PROMPTS_QUERY_KEY(filters),
      );

      queryClient.setQueryData(GET_PROMPTS_QUERY_KEY(filters), (old: any) => {
        if (!old) return old;

        if (old.pages) {
          const newData = {
            ...old,
            pages: old.pages.map((page: any) => {
              return {
                ...page,
                items: page.items
                  ? page.items.map((prompt: Prompt) => {
                      if (prompt.id === id) {
                        const updatedPrompt = {
                          ...prompt,
                          ...data,
                          version: prompt.version
                            ? String(Number(prompt.version) + 1)
                            : prompt.version,
                          updatedAt: new Date().toISOString(),
                        };
                        return updatedPrompt;
                      }
                      return prompt;
                    })
                  : page.items,
              };
            }),
          };
          return newData;
        }

        return old;
      });

      return { previousPrompts };
    },
    onError: (_, __, context) => {
      if (context?.previousPrompts) {
        queryClient.setQueryData(
          GET_PROMPTS_QUERY_KEY(filters),
          context.previousPrompts,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: GET_PROMPTS_QUERY_KEY(filters),
      });
    },
  });

  return {
    updatePrompt: mutateAsync,
    isUpdating: isPending,
  };
}
